import { buildPartLabels, buildOffcutLabels } from '../lib/cutMapLabels';
import type { EdgeAssignment, GrooveAssignment } from '../model/types';

/** Цвет подписи имени детали (вербатим прототипа: warm-brown — owner override). */
const PART_STROKE = 'var(--text-primary)';

/** Стороны детали в порядке отрисовки кромок (вербатим прототип app.js:630-645). */
type EdgeSide = 'top' | 'right' | 'bottom' | 'left';
const EDGE_SIDES: readonly EdgeSide[] = ['top', 'right', 'bottom', 'left'];

/**
 * Геометрия линии кромки в ЛОКАЛЬНОЙ системе детали (origin = верх-лево, w×h),
 * вербатим прототип `app.js:630-645`: линия с отступом 8 от углов вдоль стороны.
 *
 * ⚠️ FLIP-Y: позиция группы детали флипается (`svgY = sheetWidth − y − h`), но линии
 * рисуются в локальной системе как в прототипе (top → y=0, bottom → y=h). Если на
 * превью верх/низ кромок перепутаны — поменять местами координаты 'top' и 'bottom' ЗДЕСЬ.
 */
function edgeLineCoords(
  side: EdgeSide,
  w: number,
  h: number,
): { x1: number; y1: number; x2: number; y2: number } {
  // Зазор от рамки детали: линия кромки смещена ВНУТРЬ (по образцу владельца
  // «кромка.jpg»), иначе она сливается с рамкой детали. Стартовое значение —
  // тюнинг на превью. Зажим сверху (≤ min/2 − 2) страхует от вырождения линии
  // (x2<x1) на очень узких деталях.
  const off = Math.min(Math.max(20, Math.min(w, h) * 0.08), Math.min(w, h) / 2 - 2);
  switch (side) {
    case 'top':
      return { x1: off, y1: off, x2: w - off, y2: off };
    case 'right':
      return { x1: w - off, y1: off, x2: w - off, y2: h - off };
    case 'bottom':
      return { x1: off, y1: h - off, x2: w - off, y2: h - off };
    case 'left':
      return { x1: off, y1: off, x2: off, y2: h - off };
  }
}

/**
 * Геометрия линии паза в ЛОКАЛЬНОЙ системе детали (origin = верх-лево, w×h). Тот
 * же паттерн, что `edgeLineCoords`, но инсет ГЛУБЖЕ внутрь детали (`off = max(40,
 * *0.16)` против `max(20, *0.08)` у кромки) — чтобы линия паза не наезжала на
 * линию кромки той же стороны. Зажим сверху (≤ min/2 − 2) страхует от вырождения
 * (x2<x1 / y2<y1) на очень узких деталях.
 */
function grooveLineCoords(
  side: EdgeSide,
  w: number,
  h: number,
): { x1: number; y1: number; x2: number; y2: number } {
  const off = Math.min(Math.max(40, Math.min(w, h) * 0.16), Math.min(w, h) / 2 - 2);
  switch (side) {
    case 'top':
      return { x1: off, y1: off, x2: w - off, y2: off };
    case 'right':
      return { x1: w - off, y1: off, x2: w - off, y2: h - off };
    case 'bottom':
      return { x1: off, y1: h - off, x2: w - off, y2: h - off };
    case 'left':
      return { x1: off, y1: off, x2: off, y2: h - off };
  }
}

interface PartShapeProps {
  w: number;
  h: number;
  name: string;
  num?: number;
  edges?: EdgeAssignment;
  grooves?: GrooveAssignment;
  showEdges?: boolean;
  showGrooves?: boolean;
}

export function PartShape({
  w,
  h,
  name,
  num,
  edges,
  grooves,
  showEdges = true,
  showGrooves = true,
}: PartShapeProps) {
  const { groupTransform, texts } = buildPartLabels(w, h, name, num);
  return (
    <>
      <rect x={0} y={0} width={w} height={h} fill="#ffffff" stroke={PART_STROKE} strokeWidth={3} />
      {showEdges &&
        edges &&
        EDGE_SIDES.filter((s) => edges[s] !== '').map((s) => {
          const c = edgeLineCoords(s, w, h);
          const solid = edges[s] === 'type1';
          const sw = Math.max(4, Math.min(w, h) * 0.02);
          return (
            <line
              key={`edge-${s}`}
              className="part-edge"
              x1={c.x1}
              y1={c.y1}
              x2={c.x2}
              y2={c.y2}
              stroke="#000000"
              strokeWidth={sw}
              {...(solid ? {} : { strokeDasharray: `${sw * 2},${sw * 1.5}` })}
            />
          );
        })}
      {showGrooves &&
        grooves &&
        EDGE_SIDES.filter((s) => grooves[s] !== '').map((s) => {
          const c = grooveLineCoords(s, w, h);
          const sw = Math.max(4, Math.min(w, h) * 0.02);
          return (
            <line
              key={`groove-${s}`}
              className="part-groove"
              x1={c.x1}
              y1={c.y1}
              x2={c.x2}
              y2={c.y2}
              stroke="#000000"
              strokeWidth={sw}
              strokeDasharray={`${sw * 3},${sw * 1.5},${sw * 0.6},${sw * 1.5}`}
            />
          );
        })}
      <g className="part-labels" transform={groupTransform}>
        {texts.map((t) => (
          <text
            key={t.key}
            className={t.cls}
            x={t.x}
            y={t.y}
            transform={t.transform}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={t.fontSize}
            fill={PART_STROKE}
          >
            {t.content}
          </text>
        ))}
      </g>
    </>
  );
}

interface OffcutShapeProps {
  w: number;
  h: number;
  num?: number;
}

export function OffcutShape({ w, h, num }: OffcutShapeProps) {
  const { groupTransform, texts } = buildOffcutLabels(w, h, num);
  return (
    <>
      {/* Inline fill/stroke нужны, т.к. компонент используется и вне `.sc-svg-canvas`
          (этикетки `LabelCard`), где scoped-CSS `.free-rect` не матчится. На ЭТИКЕТКЕ →
          белый фон + сплошная тёмная рамка как у детали (решение владельца: экономия
          краски принтера). На КАРТЕ безопасно: CSS-правило
          `.free-rect{fill:#e0e0e0;stroke:var(--text-muted);stroke-width:2;stroke-dasharray:12 8}`
          ПЕРЕБИВАЕТ inline presentation-атрибуты → остаток на карте остаётся серым с
          пунктирной muted-рамкой, вид карты не меняется. */}
      <rect
        className="free-rect"
        x={0}
        y={0}
        width={w}
        height={h}
        fill="#ffffff"
        stroke={PART_STROKE}
        strokeWidth={3}
      />
      <g className="free-rect-labels" transform={groupTransform}>
        {texts.map((t) => (
          <text
            key={t.key}
            className={t.cls}
            x={t.x}
            y={t.y}
            transform={t.transform}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={t.fontSize}
          >
            {t.content}
          </text>
        ))}
      </g>
    </>
  );
}
